import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MessageSquare,
  Megaphone,
  FileText,
  Play,
  Plus,
  XCircle,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { humanizeError } from '@/lib/apiClient';
import {
  useCampaign,
  useCampaigns,
  useCancelCampaign,
  useDeleteTemplate,
  useMessages,
  useSendCampaign,
  useTemplates,
} from '../hooks';
import type { Campaign, CampaignStatus, CommunicationTemplate } from '../types';

export function CommunicationHomePage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Communication"
        description="Transactional & engagement templates, push/in-app messages, and audience-targeted campaigns."
        actions={
          <Button asChild size="sm">
            <Link to="/communication/campaigns/new">
              <Plus className="mr-1 h-3.5 w-3.5" /> New campaign
            </Link>
          </Button>
        }
      />
      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns">
            <Megaphone className="mr-1 h-4 w-4" /> Campaigns
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="mr-1 h-4 w-4" /> Templates
          </TabsTrigger>
          <TabsTrigger value="messages">
            <MessageSquare className="mr-1 h-4 w-4" /> Messages
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-3">
          <CampaignsTab />
        </TabsContent>
        <TabsContent value="templates" className="space-y-3">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="messages" className="space-y-3">
          <MessagesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function campaignStatusVariant(s: CampaignStatus): 'default' | 'success' | 'warning' | 'outline' {
  if (s === 'sent') return 'success';
  if (s === 'sending') return 'warning';
  if (s === 'cancelled') return 'outline';
  return 'default';
}

function CampaignsTab() {
  const navigate = useNavigate();
  const q = useCampaigns({ limit: 50 });
  const [send, setSend] = React.useState<Campaign | null>(null);
  const [cancel, setCancel] = React.useState<Campaign | null>(null);
  const sendMut = useSendCampaign();
  const cancelMut = useCancelCampaign();

  if (q.isLoading) return <LoadingState />;
  if (q.isError)
    return (
      <ErrorState
        title="Campaigns failed"
        description={humanizeError(q.error)}
        actionLabel="Retry"
        onAction={() => q.refetch()}
      />
    );

  const campaigns = q.data?.campaigns ?? [];

  return (
    <>
      <Card>
        <CardContent className="p-0">
          {campaigns.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No campaigns yet.
            </p>
          ) : (
            <ul className="divide-y">
              {campaigns.map((c) => (
                <li
                  key={c.campaignId}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-4 py-3 text-sm"
                >
                  <button
                    className="min-w-0 text-left hover:underline"
                    onClick={() =>
                      navigate(`/communication/campaigns/${encodeURIComponent(c.campaignId)}`)
                    }
                  >
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {c.templateSlug ?? '(custom content)'} · {c.channels.join(', ')} ·{' '}
                      {c.createdAt.slice(0, 10)} · by {c.createdBy.slice(0, 8)}…
                    </div>
                  </button>
                  <Badge variant={campaignStatusVariant(c.status)}>{c.status}</Badge>
                  <div className="text-xs text-muted-foreground">
                    {c.stats.pushSent}/{c.stats.targeted} sent
                  </div>
                  <div className="flex items-center gap-1">
                    {c.status === 'draft' && (
                      <Button size="sm" onClick={() => setSend(c)}>
                        <Play className="mr-1 h-3.5 w-3.5" /> Send
                      </Button>
                    )}
                    {(c.status === 'draft' || c.status === 'sending') && (
                      <Button size="sm" variant="outline" onClick={() => setCancel(c)}>
                        <XCircle className="mr-1 h-3.5 w-3.5" /> Cancel
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {send && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setSend(null)}
          title={`Send campaign "${send.name}"?`}
          description={`Will target ${send.stats.targeted} users. Not undoable.`}
          typedConfirmation={send.name}
          confirmLabel="Send"
          submitting={sendMut.isPending}
          onConfirm={async () => {
            await sendMut.mutateAsync(send.campaignId);
            setSend(null);
          }}
        />
      )}
      {cancel && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setCancel(null)}
          title={`Cancel campaign "${cancel.name}"?`}
          description="Stops any pending sends. Already-sent notifications remain."
          reasonRequired={false}
          confirmLabel="Cancel campaign"
          submitting={cancelMut.isPending}
          onConfirm={async () => {
            await cancelMut.mutateAsync(cancel.campaignId);
            setCancel(null);
          }}
        />
      )}
    </>
  );
}

function TemplatesTab() {
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const q = useTemplates(includeInactive);
  const [toDelete, setToDelete] = React.useState<CommunicationTemplate | null>(null);
  const del = useDeleteTemplate();

  if (q.isLoading) return <LoadingState />;
  if (q.isError)
    return (
      <ErrorState
        title="Templates failed"
        description={humanizeError(q.error)}
        actionLabel="Retry"
        onAction={() => q.refetch()}
      />
    );

  const templates = q.data?.templates ?? [];

  return (
    <>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include inactive
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          {templates.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No templates.
            </p>
          ) : (
            <ul className="divide-y">
              {templates.map((t) => (
                <li key={t.templateId} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{t.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      <code>{t.slug}</code> · {t.category} · {t.defaultChannels.join(', ')}
                      {t.targetRoute && ` · → ${t.targetRoute}`}
                    </div>
                  </div>
                  {!t.isActive && <Badge variant="outline">Inactive</Badge>}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setToDelete(t)}
                    disabled={!t.isActive}
                    aria-label={`Deactivate ${t.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {toDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setToDelete(null)}
          title={`Deactivate template "${toDelete.name}"?`}
          description="Soft-delete — template becomes unusable but is retained for audit."
          destructive
          reasonRequired={false}
          confirmLabel="Deactivate"
          submitting={del.isPending}
          onConfirm={async () => {
            await del.mutateAsync(toDelete.templateId);
            setToDelete(null);
          }}
        />
      )}
    </>
  );
}

function MessagesTab() {
  const q = useMessages(50);
  if (q.isLoading) return <LoadingState />;
  if (q.isError)
    return (
      <ErrorState
        title="Messages failed"
        description={humanizeError(q.error)}
        actionLabel="Retry"
        onAction={() => q.refetch()}
      />
    );
  const messages = q.data?.messages ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent messages</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {messages.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            No messages.
          </p>
        ) : (
          <ul className="divide-y text-sm">
            {messages.map((m) => (
              <li key={m.messageId} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {m.content.push?.title ?? m.content.inApp?.title ?? '(no title)'}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.origin} · {m.templateSlug ?? 'custom'} · {m.targeting.type} ·{' '}
                      {m.createdAt.slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    {m.stats.pushSent}/{m.stats.totalRecipients} push · {m.stats.inboxCreated} inbox
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function CampaignDetailPage() {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const q = useCampaign(campaignId);

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data)
    return (
      <ErrorState
        title="Campaign failed"
        description={q.error ? humanizeError(q.error) : 'Not found.'}
      />
    );

  const c = q.data.campaign;
  return (
    <div className="space-y-4">
      <Link
        to="/communication"
        className="inline-flex text-sm text-muted-foreground hover:text-foreground"
      >
        ← Back to communication
      </Link>
      <PageHeader
        title={c.name}
        description={`${c.channels.join(', ')} · created ${c.createdAt.slice(0, 10)} · ${c.campaignId}`}
        actions={<Badge variant={campaignStatusVariant(c.status)}>{c.status}</Badge>}
      />
      <Card>
        <CardContent className="p-4">
          <pre className="overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
            {JSON.stringify(c, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

